import React, { useEffect, useState } from "react";
import Modal from "../../../shared/components/Modal";
import JobApplicationClient from "../data/api/JobApplicationClient";
import Select from "react-select";

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '40px',
    height: '40px',
    border: state.isFocused ? '1.5px solid var(--primary)' : '1px solid #cbd5e1',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontFamily: 'inherit',
    boxShadow: state.isFocused ? '0 0 0 3px var(--primary-ghost)' : 'none',
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  menu: (base: any) => ({ ...base, fontSize: '0.85rem' }),
};

interface RejectModalProps {
  open: boolean;
  title?: string;
  candidateName: string;
  onClose: () => void;
  onConfirm: (reasonType: string, customReason?: string) => Promise<void>;
}

const RejectModal: React.FC<RejectModalProps> = ({
  open,
  title = "Reject Applicant",
  candidateName,
  onClose,
  onConfirm,
}) => {
  const [reasons, setReasons] = useState<string[]>([]);
  const [reason, setReason] = useState<string>("");
  const [customReason, setCustomReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError(null);
    setReason("");
    setCustomReason("");
    JobApplicationClient.getRejectionReasons()
      .then((data: any) => {
        if (!active) return;
        setReasons(data.rejection_reasons || (Array.isArray(data) ? data : []));
      })
      .catch(() => {
        if (!active) return;
        setReasons([]);
        setError("Failed to load rejection reasons.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open]);

  const showCustom = reason === "Other";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) {
      setError("Please select a rejection reason.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onConfirm(reason, showCustom ? customReason.trim() || undefined : undefined);
      onClose();
    } catch (err: any) {
      setError(err?.message || "Failed to reject applicant.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={title}
      onClose={saving ? () => {} : onClose}
      width="440px"
    >
      <p style={{ fontSize: "0.85rem", color: "#475569", margin: "0 0 12px 0" }}>
        Rejecting <strong>{candidateName || "this applicant"}</strong>. Please
        provide a reason for the rejection.
      </p>

      <form onSubmit={handleSubmit}>
        {error && <div className="kan-form-error">{error}</div>}

        <div className="kan-form-group">
          <label className="kan-form-label">Rejection Reason</label>
          {loading ? (
            <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>Loading reasons...</div>
          ) : (
            <Select
              options={reasons.map((r) => ({ value: r, label: r }))}
              value={reason ? { value: reason, label: reason } : null}
              onChange={(val: any) => setReason(val?.value || "")}
              isDisabled={saving}
              styles={selectStyles}
              menuPortalTarget={document.body}
              placeholder="Select a reason"
            />
          )}
        </div>

        {showCustom && (
          <div className="kan-form-group">
            <label className="kan-form-label">Custom Reason</label>
            <textarea
              className="kan-textarea"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Describe the reason for rejection..."
              disabled={saving}
            />
          </div>
        )}

        <button type="submit" className="kan-form-submit" disabled={saving || loading} style={{ background: "#dc2626" }}>
          {saving ? "Rejecting..." : "Reject Applicant"}
        </button>
      </form>
    </Modal>
  );
};

export default RejectModal;
